import { lumi } from '@/lib/lumi'

const STORAGE_KEY = 'hfcursos-progress-v1'

export interface Enrollment {
  courseId: string
  fullName: string
  email: string
  phone: string
  note: string
  enrolledAt: string
  completedModules: string[]
  completedAt: string | null
}

interface Store {
  [userId: string]: Enrollment[]
}

function readStore(): Store {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Store
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeStore(store: Store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
}

export function listEnrollments(userId: string): Enrollment[] {
  return readStore()[userId] ?? []
}

export function getEnrollment(userId: string, courseId: string): Enrollment | undefined {
  return listEnrollments(userId).find((item) => item.courseId === courseId)
}

export function enrollInCourse(
  userId: string,
  payload: Omit<Enrollment, 'enrolledAt' | 'completedModules' | 'completedAt'>,
): Enrollment {
  const store = readStore()
  const current = store[userId] ?? []
  const existing = current.find((item) => item.courseId === payload.courseId)
  if (existing) return existing

  const enrollment: Enrollment = {
    ...payload,
    enrolledAt: new Date().toISOString(),
    completedModules: [],
    completedAt: null,
  }
  store[userId] = [...current, enrollment]
  writeStore(store)
  return enrollment
}

export function markModuleComplete(
  userId: string,
  courseId: string,
  moduleId: string,
  totalModules: number,
): Enrollment | undefined {
  const store = readStore()
  const current = store[userId] ?? []
  const index = current.findIndex((item) => item.courseId === courseId)
  if (index < 0) return undefined

  const enrollment = current[index]
  const completedModules = enrollment.completedModules.includes(moduleId)
    ? enrollment.completedModules
    : [...enrollment.completedModules, moduleId]
  const completedAt =
    completedModules.length >= totalModules ? enrollment.completedAt ?? new Date().toISOString() : null

  const next: Enrollment = { ...enrollment, completedModules, completedAt }
  current[index] = next
  store[userId] = current
  writeStore(store)
  return next
}

export function progressPercent(enrollment: Enrollment | undefined, totalModules: number) {
  if (!enrollment || totalModules <= 0) return 0
  return Math.min(100, Math.round((enrollment.completedModules.length / totalModules) * 100))
}

export interface RemoteCourseProgress {
  _id: string
  userId: string
  courseId: string
  courseSlug: string
  completedModules: string[]
  completedAt?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

function nowIso() {
  return new Date().toISOString()
}

/** Consulta el avance remoto (MongoDB) de un alumno en un curso, para que sea visible en administración. */
export async function fetchRemoteProgress(userId: string, courseId: string) {
  const { list } = await lumi.entities.course_progress.list({
    filter: { userId, courseId },
    sort: { updatedAt: -1 },
    limit: 1,
  })
  return (list?.[0] as RemoteCourseProgress | undefined) ?? undefined
}

/** Consulta el avance remoto de todos los alumnos, usado por la vista de administración. */
export async function fetchAllCourseProgress() {
  const { list } = await lumi.entities.course_progress.list({
    sort: { updatedAt: -1 },
    limit: 500,
  })
  return (list ?? []) as RemoteCourseProgress[]
}

/** Guarda (crea o actualiza) el avance de módulos del alumno en MongoDB para que el avance del curso se actualice también para administración. */
export async function syncCourseProgress(
  userId: string,
  courseId: string,
  courseSlug: string,
  completedModules: string[],
  completedAt: string | null,
) {
  const existing = await fetchRemoteProgress(userId, courseId)
  const stamp = nowIso()
  if (existing) {
    return (await lumi.entities.course_progress.update(existing._id, {
      completedModules,
      completedAt: completedAt || undefined,
      updatedAt: stamp,
    })) as RemoteCourseProgress
  }
  return (await lumi.entities.course_progress.create({
    userId,
    courseId,
    courseSlug,
    completedModules,
    completedAt: completedAt || undefined,
    creator: userId,
    createdAt: stamp,
    updatedAt: stamp,
  })) as RemoteCourseProgress
}

/** Combina el avance remoto con el local (por si el alumno avanzó desde otro dispositivo) sin perder módulos ya marcados. */
export function applyRemoteProgress(
  userId: string,
  courseId: string,
  remoteCompletedModules: string[],
  remoteCompletedAt: string | null,
): Enrollment | undefined {
  const store = readStore()
  const current = store[userId] ?? []
  const index = current.findIndex((item) => item.courseId === courseId)
  if (index < 0) return undefined

  const enrollment = current[index]
  const completedModules = Array.from(new Set([...enrollment.completedModules, ...remoteCompletedModules]))
  const completedAt = enrollment.completedAt || remoteCompletedAt || null
  const next: Enrollment = { ...enrollment, completedModules, completedAt }
  current[index] = next
  store[userId] = current
  writeStore(store)
  return next
}
